import {
  Checkbox,
  FormControlLabel,
  Grid,
  Slider,
  TextField,
} from "@mui/material";
import React, { memo, useEffect, useState } from "react";
import "./BayesianOptimizationInputs.css";

function valuetextEpoch(value) {
  return `${value}Epoch`;
}

function valuetextBatchSize(value) {
  return `${value}Batch`;
}

function valuetextLR(value) {
  return `${value}LR`;
}

function valuetextLRM(value) {
  return `${value}LRM`;
}

function valuetextRank(value) {
  return `${value}Rank`;
}

function valuetextalphaRank(value) {
  return `${value}AR`;
}

const minDistanceEpoch = 0;
const minDistanceBatch = 0;
const minDistanceLR = 0;
const minDistanceLRM = 0;
const minDistanceRank = 0;
const minDistancealphaRank = 0;

const BayesianOptimizationInputs = memo(
  ({ botData, setBotData, llm_select, nodeId }) => {
    const [epoch, setEpoch] = useState([1, 3]);
    const [batchSize, setbatchSize] = useState([1, 50]);

    const [learningRate, setlearningRate] = useState([0.00001, 0.1]);
    const [learningRateMultiplier, setlearningRateMultiplier] = useState([
      1, 3,
    ]);

    const [useTextInput, setUseTextInput] = useState(false);

    const [startLR, setStartLR] = useState(0.00001);
    const [endLR, setEndLR] = useState(0.1);

    const [startLRM, setStartLRM] = useState(1);
    const [endLRM, setEndLRM] = useState(3);

    const [rank, setRank] = useState([1, 512]);
    const [alphaRank, setAlphaRank] = useState([0.5, 1]);

    useEffect(() => {
      // Helper function to parse JSON safely
      const loadData = async () => {
        try {
          // Load other states from localStorage as before
          const updateStateFromLocalStorage = (key, setter) => {
            const storedValue = localStorage.getItem(key);
            if (storedValue) {
              const parsedValue = JSON.parse(storedValue);
              if (parsedValue) {
                setter(parsedValue);
              }
            }
          };

          // Update states from localStorage
          await updateStateFromLocalStorage(`epoch_${nodeId}`, setEpoch);
          await updateStateFromLocalStorage(
            `batchSize_${nodeId}`,
            setbatchSize
          );
          await updateStateFromLocalStorage(
            `learningRate_${nodeId}`,
            setlearningRate
          );
          await updateStateFromLocalStorage(
            `learningRateMultiplier_${nodeId}`,
            setlearningRateMultiplier
          );
          await updateStateFromLocalStorage(`startLR_${nodeId}`, setStartLR);
          await updateStateFromLocalStorage(`endLR_${nodeId}`, setEndLR);
          await updateStateFromLocalStorage(`startLRM_${nodeId}`, setStartLRM);
          await updateStateFromLocalStorage(`endLRM_${nodeId}`, setEndLRM);
          await updateStateFromLocalStorage(`rank_${nodeId}`, setRank);
          await updateStateFromLocalStorage(
            `alphaRank_${nodeId}`,
            setAlphaRank
          );
          // Update botData with the new values
          setBotData((prevBotData) => ({
            ...prevBotData,
            workflow: {
              ...prevBotData.workflow,
              [`${nodeId}`]: {
                ...prevBotData.workflow[`${nodeId}`],
                finetuning_values: {
                  ...prevBotData.workflow[`${nodeId}`].finetuning_values,

                  epoch: epoch,
                  learningRate: learningRate,
                  batchSize: batchSize,
                },
              },
            },
          }));
        } catch (error) {
          console.error("Error loading data:", error);
        }
      };

      loadData();
    }, [nodeId, setBotData]);

    useEffect(() => {
      // Initialize batchSize as an array for slider compatibility
      const initialBatchSize =
        botData?.workflow?.[nodeId]?.finetuning_values?.batchSize;

      if (initialBatchSize && Array.isArray(initialBatchSize)) {
        setbatchSize(initialBatchSize);
      }
    }, [botData, nodeId]);

    const handleChangeEpoch = (_event, newValue, activeThumb) => {
      if (!Array.isArray(newValue)) {
        return;
      }

      if (activeThumb === 0) {
        setEpoch([
          Math.min(newValue[0], epoch[1] - minDistanceEpoch),
          epoch[1],
        ]);
        localStorage.setItem(
          `epoch_${nodeId}`,
          JSON.stringify([
            Math.min(newValue[0], epoch[1] - minDistanceEpoch),
            epoch[1],
          ])
        );
        setBotData((prevData) => ({
          ...prevData,
          workflow: {
            ...prevData.workflow,
            [`${nodeId}`]: {
              ...prevData.workflow[`${nodeId}`],
              finetuning_values: {
                [llm_select]: {
                  ...prevData.workflow[`${nodeId}`].finetuning_values[
                    llm_select
                  ],
                  epoch: [
                    Math.min(newValue[0], epoch[1] - minDistanceEpoch),
                    epoch[1],
                  ],
                },
              },
            },
          },
        }));
      } else {
        setEpoch([
          epoch[0],
          Math.max(newValue[1], epoch[0] + minDistanceEpoch),
        ]);
        localStorage.setItem(
          `epoch_${nodeId}`,
          JSON.stringify([
            epoch[0],
            Math.max(newValue[1], epoch[0] + minDistanceEpoch),
          ])
        );
        setBotData((prevData) => ({
          ...prevData,
          workflow: {
            ...prevData.workflow,
            [`${nodeId}`]: {
              ...prevData.workflow[`${nodeId}`],
              finetuning_values: {
                [llm_select]: {
                  ...prevData.workflow[`${nodeId}`].finetuning_values[
                    llm_select
                  ],
                  epoch: [
                    epoch[0],
                    Math.max(newValue[1], epoch[0] + minDistanceEpoch),
                  ],
                },
              },
            },
          },
        }));
      }
    };

    const handleChangeBatchSize = (_event, newValue, activeThumb) => {
      if (!Array.isArray(newValue)) {
        return;
      }

      if (activeThumb === 0) {
        setbatchSize([
          Math.min(newValue[0], batchSize[1] - minDistanceBatch),
          batchSize[1],
        ]);
        localStorage.setItem(
          `batchSize_${nodeId}`,
          JSON.stringify([
            Math.min(newValue[0], batchSize[1] - minDistanceBatch),
            batchSize[1],
          ])
        );
        setBotData((prevData) => ({
          ...prevData,
          workflow: {
            ...prevData.workflow,
            [`${nodeId}`]: {
              ...prevData.workflow[`${nodeId}`],
              finetuning_values: {
                [llm_select]: {
                  ...prevData.workflow[`${nodeId}`].finetuning_values[
                    llm_select
                  ],
                  batchSize: [
                    Math.min(newValue[0], batchSize[1] - minDistanceBatch),
                    batchSize[1],
                  ],
                },
              },
            },
          },
        }));
      } else {
        setbatchSize([
          batchSize[0],
          Math.max(newValue[1], batchSize[0] + minDistanceBatch),
        ]);
        localStorage.setItem(
          `batchSize_${nodeId}`,
          JSON.stringify([
            batchSize[0],
            Math.max(newValue[1], batchSize[0] + minDistanceBatch),
          ])
        );
        setBotData((prevData) => ({
          ...prevData,
          workflow: {
            ...prevData.workflow,
            [`${nodeId}`]: {
              ...prevData.workflow[`${nodeId}`],
              finetuning_values: {
                [llm_select]: {
                  ...prevData.workflow[`${nodeId}`].finetuning_values[
                    llm_select
                  ],
                  batchSize: [
                    batchSize[0],
                    Math.max(newValue[1], batchSize[0] + minDistanceBatch),
                  ],
                },
              },
            },
          },
        }));
      }
    };

    const handleChangeLR = (_event, newValue, activeThumb) => {
      if (!Array.isArray(newValue)) {
        return;
      }

      if (activeThumb === 0) {
        setlearningRate([
          Math.min(newValue[0], learningRate[1] - minDistanceLR),
          learningRate[1],
        ]);
        localStorage.setItem(
          `learningRate_${nodeId}`,
          JSON.stringify([
            Math.min(newValue[0], learningRate[1] - minDistanceLR),
            learningRate[1],
          ])
        );
        setBotData((prevData) => ({
          ...prevData,
          workflow: {
            ...prevData.workflow,
            [`${nodeId}`]: {
              ...prevData.workflow[`${nodeId}`],
              finetuning_values: {
                [llm_select]: {
                  ...prevData.workflow[`${nodeId}`].finetuning_values[
                    llm_select
                  ],
                  learningRate: [
                    Math.min(newValue[0], learningRate[1] - minDistanceLR),
                    learningRate[1],
                  ],
                },
              },
            },
          },
        }));
      } else {
        setlearningRate([
          learningRate[0],
          Math.max(newValue[1], learningRate[0] + minDistanceLR),
        ]);
        localStorage.setItem(
          `learningRate_${nodeId}`,
          JSON.stringify([
            learningRate[0],
            Math.max(newValue[1], learningRate[0] + minDistanceLR),
          ])
        );

        setBotData((prevData) => ({
          ...prevData,
          workflow: {
            ...prevData.workflow,
            [`${nodeId}`]: {
              ...prevData.workflow[`${nodeId}`],
              finetuning_values: {
                [llm_select]: {
                  ...prevData.workflow[`${nodeId}`].finetuning_values[
                    llm_select
                  ],
                  learningRate: [
                    learningRate[0],
                    Math.max(newValue[1], learningRate[0] + minDistanceLR),
                  ],
                },
              },
            },
          },
        }));
      }
    };

    const handleChangeLRM = (_event, newValue, activeThumb) => {
      if (!Array.isArray(newValue)) {
        return;
      }

      if (activeThumb === 0) {
        setlearningRateMultiplier([
          Math.min(newValue[0], learningRateMultiplier[1] - minDistanceLRM),
          learningRateMultiplier[1],
        ]);
        localStorage.setItem(
          `learningRateMultiplier_${nodeId}`,
          JSON.stringify([
            Math.min(newValue[0], learningRateMultiplier[1] - minDistanceLRM),
            learningRateMultiplier[1],
          ])
        );

        setBotData((prevData) => ({
          ...prevData,
          workflow: {
            ...prevData.workflow,
            [`${nodeId}`]: {
              ...prevData.workflow[`${nodeId}`],
              finetuning_values: {
                [llm_select]: {
                  ...prevData.workflow[`${nodeId}`].finetuning_values[
                    llm_select
                  ],
                  learningRateMultiplier: [
                    Math.min(
                      newValue[0],
                      learningRateMultiplier[1] - minDistanceLRM
                    ),
                    learningRateMultiplier[1],
                  ],
                },
              },
            },
          },
        }));
      } else {
        setlearningRateMultiplier([
          learningRateMultiplier[0],
          Math.max(newValue[1], learningRateMultiplier[0] + minDistanceLRM),
        ]);
        localStorage.setItem(
          `learningRateMultiplier_${nodeId}`,
          JSON.stringify([
            Math.min(newValue[0], learningRateMultiplier[1] - minDistanceLRM),
            learningRateMultiplier[1],
          ])
        );

        setBotData((prevData) => ({
          ...prevData,
          workflow: {
            ...prevData.workflow,
            [`${nodeId}`]: {
              ...prevData.workflow[`${nodeId}`],
              finetuning_values: {
                [llm_select]: {
                  ...prevData.workflow[`${nodeId}`].finetuning_values[
                    llm_select
                  ],
                  learningRate: [
                    learningRateMultiplier[0],
                    Math.max(
                      newValue[1],
                      learningRateMultiplier[0] + minDistanceLRM
                    ),
                  ],
                },
              },
            },
          },
        }));
      }
    };

    const handleChangeRank = (_event, newValue, activeThumb) => {
      if (!Array.isArray(newValue)) {
        return;
      }

      if (activeThumb === 0) {
        setRank([Math.min(newValue[0], rank[1] - minDistanceRank), rank[1]]);
        localStorage.setItem(
          `rank_${nodeId}`,
          JSON.stringify([
            Math.min(newValue[0], rank[1] - minDistanceRank),
            rank[1],
          ])
        );

        setBotData((prevData) => ({
          ...prevData,
          workflow: {
            ...prevData.workflow,
            [`${nodeId}`]: {
              ...prevData.workflow[`${nodeId}`],
              finetuning_values: {
                [llm_select]: {
                  ...prevData.workflow[`${nodeId}`].finetuning_values[
                    llm_select
                  ],
                  rank: [
                    Math.min(newValue[0], rank[1] - minDistanceRank),
                    rank[1],
                  ],
                },
              },
            },
          },
        }));
      } else {
        setRank([rank[0], Math.max(newValue[1], rank[0] + minDistanceRank)]);
        localStorage.setItem(
          `rank_${nodeId}`,
          JSON.stringify([
            rank[0],
            Math.max(newValue[1], rank[0] + minDistanceRank),
          ])
        );

        setBotData((prevData) => ({
          ...prevData,
          workflow: {
            ...prevData.workflow,
            [`${nodeId}`]: {
              ...prevData.workflow[`${nodeId}`],
              finetuning_values: {
                [llm_select]: {
                  ...prevData.workflow[`${nodeId}`].finetuning_values[
                    llm_select
                  ],
                  rank: [
                    rank[0],
                    Math.max(newValue[1], rank[0] + minDistanceRank),
                  ],
                },
              },
            },
          },
        }));
      }
    };

    const handleChangealphaRank = (_event, newValue, activeThumb) => {
      if (!Array.isArray(newValue)) {
        return;
      }

      if (activeThumb === 0) {
        setAlphaRank([
          Math.min(newValue[0], alphaRank[1] - minDistancealphaRank),
          alphaRank[1],
        ]);
        setBotData((prevData) => ({
          ...prevData,
          workflow: {
            ...prevData.workflow,
            [`${nodeId}`]: {
              ...prevData.workflow[`${nodeId}`],
              finetuning_values: {
                [llm_select]: {
                  ...prevData.workflow[`${nodeId}`].finetuning_values[
                    llm_select
                  ],
                  alphaRank: [
                    Math.min(newValue[0], alphaRank[1] - minDistancealphaRank),
                    alphaRank[1],
                  ],
                },
              },
            },
          },
        }));
      } else {
        setAlphaRank([
          alphaRank[0],
          Math.max(newValue[1], alphaRank[0] + minDistancealphaRank),
        ]);
        setBotData((prevData) => ({
          ...prevData,
          workflow: {
            ...prevData.workflow,
            [`${nodeId}`]: {
              ...prevData.workflow[`${nodeId}`],
              finetuning_values: {
                [llm_select]: {
                  ...prevData.workflow[`${nodeId}`].finetuning_values[
                    llm_select
                  ],
                  alphaRank: [
                    alphaRank[0],
                    Math.max(newValue[1], alphaRank[0] + minDistancealphaRank),
                  ],
                },
              },
            },
          },
        }));
      }
    };

    const handleTextInputChange = (start, end) => {
      const newLR = [parseFloat(start), parseFloat(end)];
      setlearningRate(newLR);
      setStartLR(start);
      setEndLR(end);
      updateBotData(newLR);
    };

    const handleTextInputChangeM = (start, end) => {
      const newLR = [parseFloat(start), parseFloat(end)];
      setlearningRateMultiplier(newLR);
      setStartLRM(start);
      setEndLRM(end);
      updateBotData(newLR);
    };

    const updateBotData = (newLR) => {
      setBotData((prevData) => ({
        ...prevData,
        workflow: {
          ...prevData.workflow,
          [`${nodeId}`]: {
            ...prevData.workflow[`${nodeId}`],
            finetuning_values: {
              [llm_select]: {
                ...prevData.workflow[`${nodeId}`].finetuning_values[llm_select],
                learningRate: newLR,
              },
            },
          },
        },
      }));
    };

    return (
      <div>
        {/** ***************************************** Epoch *************************************************** **/}

        <div className="slider-component">
          <p>Epoch (1 - 5)</p>
          <Grid container spacing={2} alignItems="center">
            <Grid item>1</Grid>
            <Grid item xs>
              <Slider
                getAriaLabel={() => "Epoch"}
                value={epoch}
                max={5}
                min={1}
                onChange={handleChangeEpoch}
                valueLabelDisplay="auto"
                getAriaValueText={valuetextEpoch}
                disableSwap
                className="nodrag"
              />
            </Grid>
            <Grid item>5</Grid>
          </Grid>
        </div>

        {/** ***************************************** Batch Size *************************************************** **/}

        <div className="slider-component">
          <p>Batch Size (1 - 50)</p>
          <Grid container spacing={2} alignItems="center">
            <Grid item>1</Grid>
            <Grid item xs>
              <Slider
                getAriaLabel={() => "Batch Size"}
                value={batchSize}
                max={50}
                min={1}
                onChange={handleChangeBatchSize}
                valueLabelDisplay="auto"
                getAriaValueText={valuetextBatchSize}
                disableSwap
                className="nodrag"
                step={1}
              />
            </Grid>
            <Grid item>50</Grid>
          </Grid>
        </div>
        {/** ***************************************** Learning Rate *************************************************** **/}

        {llm_select === "llama-3-8b" ? (
          <div className="slider-component">
            <p>Learning Rate (0.00001 - 1)</p>
            <FormControlLabel
              control={
                <Checkbox
                  checked={useTextInput}
                  onChange={(e) => setUseTextInput(e.target.checked)}
                />
              }
              label="Use text input"
            />
            {useTextInput ? (
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <TextField
                    label="Start LR"
                    value={startLR}
                    onChange={(e) =>
                      handleTextInputChange(e.target.value, endLR)
                    }
                    inputProps={{ step: "0.00001", min: "0.00001", max: "0.1" }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="End LR"
                    value={endLR}
                    onChange={(e) =>
                      handleTextInputChange(startLR, e.target.value)
                    }
                    inputProps={{ step: "0.00001", min: "0.00001", max: "0.1" }}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2} alignItems="center">
                <Grid item>0.00001</Grid>
                <Grid item xs>
                  <Slider
                    getAriaLabel={() => "Learning Rate"}
                    value={learningRate}
                    max={0.1}
                    min={0.00001}
                    onChange={handleChangeLR}
                    valueLabelDisplay="auto"
                    getAriaValueText={valuetextLR}
                    disableSwap
                    className="nodrag"
                    step={0.00001}
                  />
                </Grid>
                <Grid item>0.1</Grid>
              </Grid>
            )}
          </div>
        ) : (
          <div className="slider-component">
            <p>Learning Rate Multiplier (0.1 - 10)</p>
            <FormControlLabel
              control={
                <Checkbox
                  checked={useTextInput}
                  onChange={(e) => setUseTextInput(e.target.checked)}
                />
              }
              label="Use text input"
            />
            {useTextInput ? (
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <TextField
                    label="Start LR Multiplier"
                    value={startLRM}
                    onChange={(e) =>
                      handleTextInputChangeM(e.target.value, endLRM)
                    }
                    inputProps={{ step: "0.1", min: "0.1", max: "10" }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="End LR Multiplier"
                    value={endLRM}
                    onChange={(e) =>
                      handleTextInputChangeM(startLRM, e.target.value)
                    }
                    inputProps={{ step: "0.1", min: "0.1", max: "10" }}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2} alignItems="center">
                <Grid item>0.1</Grid>
                <Grid item xs>
                  <Slider
                    getAriaLabel={() => "Learning Rate Multiplier"}
                    value={learningRateMultiplier}
                    max={10}
                    min={0.1}
                    onChange={handleChangeLRM}
                    valueLabelDisplay="auto"
                    getAriaValueText={valuetextLRM}
                    disableSwap
                    className="nodrag"
                    step={0.1}
                  />
                </Grid>
                <Grid item>10</Grid>
              </Grid>
            )}
          </div>
        )}

        {/** ***************************************** QLora Params *************************************************** **/}
        {llm_select === "llama-3-8b" && (
          <div className="qlora-container">
            <hr />

            <p>QLoRA parameters</p>
          </div>
        )}
        {llm_select === "llama-3-8b" && (
          <div className="slider-component">
            <p>Rank (1 - 512)</p>
            <Grid container spacing={2} alignItems="center">
              <Grid item>1</Grid>
              <Grid item xs>
                <Slider
                  getAriaLabel={() => "Rank"}
                  value={rank}
                  max={512}
                  min={1}
                  onChange={handleChangeRank}
                  valueLabelDisplay="auto"
                  getAriaValueText={valuetextRank}
                  disableSwap
                  className="nodrag"
                  step={1}
                />
              </Grid>
              <Grid item>512</Grid>
            </Grid>
          </div>
        )}

        {llm_select === "llama-3-8b" && (
          <div className="slider-component">
            <p>Alpha/Rank (0.1 - 2)</p>
            <Grid container spacing={2} alignItems="center">
              <Grid item>0.1</Grid>
              <Grid item xs>
                <Slider
                  getAriaLabel={() => "Aplha/Rank"}
                  value={alphaRank}
                  max={2}
                  min={0.1}
                  onChange={handleChangealphaRank}
                  valueLabelDisplay="auto"
                  getAriaValueText={valuetextalphaRank}
                  disableSwap
                  className="nodrag"
                  step={0.001}
                />
              </Grid>
              <Grid item>2</Grid>
            </Grid>
          </div>
        )}
      </div>
    );
  }
);

export default BayesianOptimizationInputs;
